<script setup lang="ts">
import { EntityProfileLeadRow, EntityProfileLeadRowSkeleton } from '~/components/entity/profile-lead'
import { FeaturePagination } from '~/components/feature/pagination'
import {
  Table,
  TableBody,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow
} from '~/components/ui/table'
import { MultiSelect } from '~/components/ui/multiselect'
import type { IMultiselectOption } from '~/components/ui/multiselect'
import { Button } from '~/components/ui/button'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-vue-next'
import { useProfileLeads } from '~/composables/useProfileLeads'
import type { TProfileLeadSortBy, TProfileLeadSortDir } from '~/composables/useProfileLeads'
import { PROFILE_LEAD_STATUS_LABELS } from '~/components/entity/profile-lead/types'
import type { TProfileLeadStatus } from '~/types/b2b'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const currentPage = ref(Number(route.query.page ?? 1))
const selectedStatuses = ref<IMultiselectOption[]>([])
const activeStatuses = ref<TProfileLeadStatus[]>([])
const sortBy = ref<TProfileLeadSortBy>('date')
const sortDir = ref<TProfileLeadSortDir>('desc')

const statusOptions = computed<IMultiselectOption[]>(() =>
  (Object.keys(PROFILE_LEAD_STATUS_LABELS) as string[]).map((value) => ({
    id: value,
    label: t(`page.b2b.leads.lead_status.${value}`),
    value,
    url: '',
  }))
)

let statusDebounceTimer: ReturnType<typeof setTimeout> | null = null

onUnmounted(() => {
  if (statusDebounceTimer) clearTimeout(statusDebounceTimer)
})

const { leads, meta, links, pending, error } = useProfileLeads(
  activeStatuses,
  currentPage,
  sortBy,
  sortDir
)

const toggleSort = (field: TProfileLeadSortBy) => {
  if (sortBy.value === field) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortBy.value = field
    sortDir.value = 'desc'
  }
  currentPage.value = 1
}

const hasActiveFilters = computed(() => selectedStatuses.value.length > 0)

const onStatusChange = (value: IMultiselectOption[]) => {
  selectedStatuses.value = value
  if (statusDebounceTimer) clearTimeout(statusDebounceTimer)
  statusDebounceTimer = setTimeout(() => {
    activeStatuses.value = value.map((o) => o.value as TProfileLeadStatus)
    currentPage.value = 1
  }, 1200)
}

const resetFilters = () => {
  selectedStatuses.value = []
  activeStatuses.value = []
  if (statusDebounceTimer) clearTimeout(statusDebounceTimer)
  currentPage.value = 1
  router.replace({ query: {} })
}

const changePage = (page: number) => {
  currentPage.value = page
  router.replace({ query: page > 1 ? { page: String(page) } : {} })
}
</script>

<template>
  <div class="mb-4 flex flex-wrap items-start gap-2">
    <div class="w-full sm:w-96 sm:shrink-0">
      <MultiSelect
        class="multiselect_height_52"
        :model-value="selectedStatuses"
        :options="statusOptions"
        :placeholder="t('page.b2b.leads.filter.status')"
        :searchable="false"
        @update:model-value="onStatusChange"
      />
    </div>
    <Button
      v-if="hasActiveFilters"
      variant="outline-blue"
      class="h-[52px] w-full sm:ml-auto sm:w-auto"
      @click="resetFilters"
    >
      {{ t('common.reset_filters') }}
    </Button>
  </div>

  <ContentBlock variant="white">
    <div class="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>{{ t('common.organization_label') }}</TableHead>
            <TableHead>
              <button class="flex items-center gap-1 hover:text-gray-900" @click="toggleSort('amount')">
                {{ t('common.amount') }}
                <ArrowUpDown v-if="sortBy !== 'amount'" class="h-3.5 w-3.5 opacity-50" />
                <ArrowUp v-else-if="sortDir === 'asc'" class="h-3.5 w-3.5" />
                <ArrowDown v-else class="h-3.5 w-3.5" />
              </button>
            </TableHead>
            <TableHead>
              <button class="flex items-center gap-1 hover:text-gray-900" @click="toggleSort('date')">
                {{ t('page.profile.leads.table.date') }}
                <ArrowUpDown v-if="sortBy !== 'date'" class="h-3.5 w-3.5 opacity-50" />
                <ArrowUp v-else-if="sortDir === 'asc'" class="h-3.5 w-3.5" />
                <ArrowDown v-else class="h-3.5 w-3.5" />
              </button>
            </TableHead>
            <TableHead>{{ t('page.b2b.leads.table.status') }}</TableHead>
            <TableHead>{{ t('page.b2b.leads.table.actions') }}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <EntityProfileLeadRowSkeleton v-if="pending" :count="5" />
          <TableEmpty v-else-if="error" :colspan="6">{{ t('page.profile.leads.table.error') }}</TableEmpty>
          <TableEmpty v-else-if="leads.length === 0" :colspan="6">{{ t('page.profile.leads.table.empty') }}</TableEmpty>
          <EntityProfileLeadRow
            v-for="lead in leads"
            v-else
            :key="lead.id"
            :lead="lead"
          />
        </TableBody>
      </Table>
    </div>
  </ContentBlock>

  <div class="mt-4 flex justify-end">
    <FeaturePagination
      v-if="meta && meta.last_page > 1"
      :meta="meta"
      :links="links"
      @change="changePage"
    />
  </div>
</template>
